import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Envelope, Lock, User, Phone, MapPin, CheckCircle, Eye, EyeSlash, GoogleLogo, AppleLogo } from '@phosphor-icons/react';
import { API } from '@/App';
import { toast } from 'sonner';
import DropioLogo from '@/components/DropioLogo';
import InAppBrowserBlockerModal from '@/components/InAppBrowserBlockerModal';
import useInAppBrowser from '@/hooks/useInAppBrowser';

const COUNTRIES = ['Australia', 'Bangladesh', 'Canada', 'India', 'New Zealand', 'Pakistan', 'Singapore', 'UAE', 'United Kingdom', 'United States', 'Other'];

const SignUpPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [blockerOpen, setBlockerOpen] = useState(false);
  const inApp = useInAppBrowser();
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', country: '', password: '', user_type: 'sender'
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreeTerms) {
      toast.error('Please accept the terms to continue');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        const msg = typeof detail === 'string' ? detail : 'Registration failed';
        throw new Error(msg);
      }
      toast.success('Welcome to Dropio!');
      navigate('/dashboard', { state: { user: data } });
    } catch (err) {
      toast.error(err.message);
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // In-app browsers (Instagram/Facebook/Messenger/TikTok) get Google's
  // `Error 403: disallowed_useragent` → intercept and route through external browser.
  const handleGoogleLogin = () => {
    if (inApp.isInApp) {
      setBlockerOpen(true);
      return;
    }
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/" data-testid="signup-logo-link">
            <DropioLogo size={48} />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="signup-heading">
              Create your account
            </h1>
            <p className="text-sm text-gray-600">Join thousands of travelers and senders</p>
          </div>

          {/* Account Type Toggle */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-lg mb-5">
            <button
              type="button"
              onClick={() => setForm({ ...form, user_type: 'sender' })}
              className={`py-2 text-sm font-semibold rounded-md transition-all ${
                form.user_type === 'sender' ? 'bg-white text-[#6D28D9] shadow-sm' : 'text-gray-600'
              }`}
              data-testid="select-sender-type"
            >
              I&apos;m a Sender
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, user_type: 'traveler' })}
              className={`py-2 text-sm font-semibold rounded-md transition-all ${
                form.user_type === 'traveler' ? 'bg-white text-[#6D28D9] shadow-sm' : 'text-gray-600'
              }`}
              data-testid="select-traveler-type"
            >
              I&apos;m a Traveler
            </button>
          </div>

          {/* In-app browser warning banner */}
          {inApp.isInApp && (
            <div
              className="mb-5 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-900 flex items-start gap-2"
              data-testid="inapp-banner"
              role="alert"
            >
              <span aria-hidden>⚠️</span>
              <div>
                You&apos;re viewing this inside <strong>{inApp.appName === 'WebView' ? 'an embedded browser' : inApp.appName}</strong>. Google Sign-In won&apos;t work here.
                <button
                  type="button"
                  onClick={() => setBlockerOpen(true)}
                  className="ml-1 underline font-semibold"
                  data-testid="inapp-banner-open"
                >Open in Safari/Chrome →</button>
              </div>
            </div>
          )}

          {/* Social Login */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <button
              onClick={handleGoogleLogin}
              className="flex items-center justify-center gap-2 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="google-signup"
            >
              <GoogleLogo size={18} weight="bold" /> <span className="text-sm font-medium">Google</span>
            </button>
            <button
              onClick={handleGoogleLogin}
              className="flex items-center justify-center gap-2 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="apple-signup"
            >
              <AppleLogo size={18} weight="fill" /> <span className="text-sm font-medium">Apple</span>
            </button>
          </div>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-200"></span>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-2 text-gray-400">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field icon={User} name="full_name" type="text" placeholder="Full Name" value={form.full_name} onChange={handleChange} required testId="signup-name" />
            <Field icon={Envelope} name="email" type="email" placeholder="Email address" value={form.email} onChange={handleChange} required testId="signup-email" />
            <Field icon={Phone} name="phone" type="tel" placeholder="Phone number" value={form.phone} onChange={handleChange} testId="signup-phone" />

            <div className="relative">
              <MapPin size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
              <select
                name="country" value={form.country} onChange={handleChange} required
                className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100 appearance-none bg-white"
                data-testid="signup-country"
              >
                <option value="">Select country</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="relative">
              <Lock size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                name="password" type={showPassword ? 'text' : 'password'}
                placeholder="Password (min 8 characters)" value={form.password} onChange={handleChange} required minLength={8}
                className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100"
                data-testid="signup-password"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#6D28D9] focus:ring-purple-200"
                data-testid="agree-terms"
              />
              <span className="text-gray-600">I agree to Dropio&apos;s <a href="#" className="text-[#6D28D9] font-semibold">Terms of Service</a> and <a href="#" className="text-[#6D28D9] font-semibold">Privacy Policy</a></span>
            </label>

            <button
              type="submit" disabled={loading}
              className="w-full dropio-gradient-cta text-white font-semibold py-3 rounded-lg shadow-lg shadow-purple-200 hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="signup-submit"
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#6D28D9] font-semibold hover:underline" data-testid="goto-login">Log In</Link>
          </p>
        </div>
      </div>
      <InAppBrowserBlockerModal
        open={blockerOpen}
        onClose={() => setBlockerOpen(false)}
        appName={inApp.appName}
        os={inApp.os}
      />
    </div>
  );
};

const Field = ({ icon: Icon, testId, ...props }) => (
  <div className="relative">
    <Icon size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
    <input
      {...props}
      className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100"
      data-testid={testId}
    />
  </div>
);

export default SignUpPage;
